import React from 'react';
import { toast as toastify } from 'react-toastify';
import { getBankAccountsSummary, createBankAccount, deleteBankAccount, transferBetweenAccounts } from '../../../../api/integrations';
import AppContext from '../../context';

const fmt = (val) =>
    Number(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const BANK_ICON_COLOR = {
    checking: '#f97316',
    savings: '#f97316',
    cash: '#10b981',
};

const ACCOUNT_TYPE_LABEL = {
    checking: 'Conta Corrente',
    savings: 'Poupança',
    cash: 'Caixa Físico',
};

// ── Estilos embutidos (sem dependência de CSS externo) ────────────────────────
const S = {
    page: { padding: '28px 32px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" },
    breadcrumb: { fontSize: 13, color: '#64748b', marginBottom: 6 },
    breadcrumbLink: { color: '#2563eb', textDecoration: 'none', cursor: 'pointer' },
    pageTitle: { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
    pageSubtitle: { fontSize: 13, color: '#64748b', marginTop: 2, marginBottom: 24 },
    summaryRow: { display: 'flex', gap: 20, marginBottom: 28, flexWrap: 'wrap' },
    consolidatedCard: {
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2240 100%)',
        borderRadius: 14, padding: '24px 28px', flex: '1 1 320px', minWidth: 280,
        color: '#fff', boxShadow: '0 4px 20px rgba(15,34,64,0.25)',
    },
    consolidatedLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 },
    consolidatedValue: { fontSize: 32, fontWeight: 800, letterSpacing: '-0.5px', margin: '4px 0' },
    consolidatedDesc: { fontSize: 12, color: '#94a3b8', marginTop: 4 },
    statCard: {
        background: '#fff', borderRadius: 14, padding: '20px 24px',
        flex: '1 1 160px', minWidth: 150, boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
        border: '1px solid #e2e8f0',
    },
    statNum: { fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1 },
    statLabel: { fontSize: 12, color: '#64748b', marginTop: 4 },
    statSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    sectionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    sectionTitle: { fontSize: 16, fontWeight: 700, color: '#0f172a' },
    btnOutline: {
        background: '#fff', border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '8px 16px',
        fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    },
    btnPrimary: {
        background: '#2563eb', border: 'none', borderRadius: 8, padding: '8px 18px',
        fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    },
    btnRow: { display: 'flex', gap: 10 },
    cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 },
    accountCard: {
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
        padding: '20px 22px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        display: 'flex', flexDirection: 'column', gap: 0, position: 'relative',
    },
    cardTopRow: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
    bankIcon: {
        width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 18, color: '#fff', flexShrink: 0,
    },
    cardName: { fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 1 },
    cardBank: { fontSize: 12, color: '#64748b' },
    ofBadge: {
        background: '#f97316', color: '#fff', fontSize: 10, fontWeight: 700,
        borderRadius: 5, padding: '2px 6px', letterSpacing: '0.05em',
    },
    badgeRow: { position: 'absolute', top: 16, right: 16, display: 'flex', gap: 6 },
    balanceLabel: { fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 },
    balanceValue: { fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 12 },
    infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 0', marginBottom: 12 },
    infoLabel: { fontSize: 11, color: '#94a3b8' },
    infoValue: { fontSize: 12, fontWeight: 600, color: '#374151' },
    statsRow: { display: 'flex', gap: 0, borderTop: '1px solid #f1f5f9', paddingTop: 12, marginTop: 4 },
    statIn: { flex: 1, fontSize: 12 },
    statOut: { flex: 1, fontSize: 12, borderLeft: '1px solid #f1f5f9', paddingLeft: 12 },
    statArrowIn: { color: '#10b981', fontWeight: 700, fontSize: 11 },
    statArrowOut: { color: '#ef4444', fontWeight: 700, fontSize: 11 },
    cardActions: { display: 'flex', gap: 8, marginTop: 14 },
    btnCardSecondary: {
        flex: 1, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7,
        padding: '7px 0', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
    },
    btnCardPrimary: {
        flex: 1, background: '#2563eb', border: 'none', borderRadius: 7,
        padding: '7px 0', fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer',
    },
    // Modal
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: {
        background: '#fff', borderRadius: 16, padding: '28px 32px', width: '100%',
        maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', position: 'relative',
    },
    modalTitle: { fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 20 },
    modalClose: {
        position: 'absolute', top: 18, right: 22, background: 'none', border: 'none',
        fontSize: 20, cursor: 'pointer', color: '#94a3b8', lineHeight: 1,
    },
    typeRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 },
    typeCard: (selected) => ({
        border: selected ? '2px solid #2563eb' : '2px solid #e2e8f0',
        borderRadius: 10, padding: '12px 16px', cursor: 'pointer', background: selected ? '#eff6ff' : '#fff',
        display: 'flex', alignItems: 'center', gap: 10,
    }),
    typeRadio: { accentColor: '#2563eb', width: 16, height: 16 },
    typeLabel: (selected) => ({ fontSize: 14, fontWeight: 600, color: selected ? '#1d4ed8' : '#374151' }),
    typeSub: { fontSize: 11, color: '#94a3b8' },
    fieldGroup: { marginBottom: 16 },
    fieldLabel: { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, display: 'block' },
    input: {
        width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px',
        fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box',
        fontFamily: 'inherit',
    },
    row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },
    btnCancel: {
        background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 20px',
        fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer',
    },
    btnSubmit: {
        background: '#2563eb', border: 'none', borderRadius: 8, padding: '9px 24px',
        fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer',
    },
    emptyState: { textAlign: 'center', padding: '40px 20px', color: '#94a3b8' },
    spinner: { textAlign: 'center', padding: '48px 0', color: '#94a3b8' },
};

// ── Componente Modal Novo Caixa ───────────────────────────────────────────────
function NovoCaixaModal({ show, onHide, onSaved }) {
    const [accountType, setAccountType] = React.useState('checking');
    const [form, setForm] = React.useState({ name: '', bank_name: '', agency: '', account_number: '', initial_balance: '' });
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (show) {
            setAccountType('checking');
            setForm({ name: '', bank_name: '', agency: '', account_number: '', initial_balance: '' });
        }
    }, [show]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            toastify.error('Nome do caixa é obrigatório.', { position: 'top-center' });
            return;
        }
        setLoading(true);
        try {
            const balance = parseFloat(String(form.initial_balance).replace(',', '.')) || 0;
            await createBankAccount({
                name: form.name.trim(),
                account_type: accountType,
                initial_balance: balance,
                bank_name: form.bank_name.trim() || null,
                agency: form.agency.trim() || null,
                account_number: form.account_number.trim() || null,
                is_open_finance: false,
            });
            toastify.success('Caixa cadastrado com sucesso!', { position: 'top-center' });
            onSaved();
            onHide();
        } catch (err) {
            toastify.error('Erro ao cadastrar caixa.', { position: 'top-center' });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onHide()}>
            <div style={S.modal}>
                <button style={S.modalClose} onClick={onHide}>×</button>
                <div style={S.modalTitle}>Novo Caixa</div>

                {/* Tipo */}
                <div style={S.fieldGroup}>
                    <label style={S.fieldLabel}>Tipo de caixa *</label>
                    <div style={S.typeRow}>
                        <div style={S.typeCard(accountType === 'checking')} onClick={() => setAccountType('checking')}>
                            <input type="radio" style={S.typeRadio} checked={accountType === 'checking'} onChange={() => setAccountType('checking')} />
                            <div>
                                <i className="fas fa-university" style={{ color: '#2563eb', marginRight: 6 }} />
                                <span style={S.typeLabel(accountType === 'checking')}>Conta Bancária</span>
                                <div style={S.typeSub}>Pode usar Open Finance</div>
                            </div>
                        </div>
                        <div style={S.typeCard(accountType === 'cash')} onClick={() => setAccountType('cash')}>
                            <input type="radio" style={S.typeRadio} checked={accountType === 'cash'} onChange={() => setAccountType('cash')} />
                            <div>
                                <i className="fas fa-cash-register" style={{ color: '#10b981', marginRight: 6 }} />
                                <span style={S.typeLabel(accountType === 'cash')}>Caixa Físico</span>
                                <div style={S.typeSub}>Conferência manual</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Nome */}
                <div style={S.fieldGroup}>
                    <label style={S.fieldLabel}>Nome do caixa *</label>
                    <input
                        style={S.input} name="name" value={form.name}
                        placeholder="Ex: Itaú PJ Principal" onChange={handleChange}
                    />
                </div>

                {/* Campos bancários (só se conta bancária) */}
                {accountType !== 'cash' && (
                    <div style={{ ...S.fieldGroup }}>
                        <div style={S.row3}>
                            <div>
                                <label style={S.fieldLabel}>Banco</label>
                                <input style={S.input} name="bank_name" value={form.bank_name} placeholder="Ex: Banco Inter" onChange={handleChange} />
                            </div>
                            <div>
                                <label style={S.fieldLabel}>Agência</label>
                                <input style={S.input} name="agency" value={form.agency} placeholder="0001" onChange={handleChange} />
                            </div>
                            <div>
                                <label style={S.fieldLabel}>Conta</label>
                                <input style={S.input} name="account_number" value={form.account_number} placeholder="12345-6" onChange={handleChange} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Saldo inicial */}
                <div style={S.fieldGroup}>
                    <label style={S.fieldLabel}>Saldo inicial</label>
                    <input
                        style={S.input} name="initial_balance" value={form.initial_balance}
                        placeholder="R$ 0,00" onChange={handleChange} type="text"
                    />
                </div>

                <div style={S.modalFooter}>
                    <button style={S.btnCancel} onClick={onHide} disabled={loading}>Cancelar</button>
                    <button style={S.btnSubmit} onClick={handleSubmit} disabled={loading}>
                        {loading ? <i className="fas fa-spinner fa-spin" /> : 'Cadastrar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Card de cada conta ────────────────────────────────────────────────────────
function AccountCard({ account, onDelete }) {
    const isCash = account.account_type === 'cash';
    const iconColor = BANK_ICON_COLOR[account.account_type] || '#2563eb';
    const iconClass = isCash ? 'fas fa-cash-register' : 'fas fa-university';

    return (
        <div style={S.accountCard}>
            {/* Badges */}
            <div style={S.badgeRow}>
                {account.is_open_finance && <span style={S.ofBadge}>OF</span>}
            </div>

            {/* Header */}
            <div style={S.cardTopRow}>
                <div style={{ ...S.bankIcon, background: iconColor }}>
                    <i className={iconClass} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={S.cardName}>{account.name}</div>
                    <div style={S.cardBank}>
                        {isCash
                            ? 'Caixa Físico'
                            : `${account.bank_name || 'Banco'} ${account.bank_code ? `(${account.bank_code})` : ''}`
                        }
                    </div>
                </div>
            </div>

            {/* Saldo */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <div style={S.balanceLabel}>SALDO ATUAL</div>
                    <div style={S.balanceValue}>{fmt(account.current_balance)}</div>
                </div>
                {account.projected_balance !== undefined && account.projected_balance !== account.current_balance && (
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ ...S.balanceLabel, color: '#64748b' }}>SALDO PROJETADO</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: account.projected_balance < 0 ? '#ef4444' : '#64748b' }}>
                            {fmt(account.projected_balance)}
                        </div>
                    </div>
                )}
            </div>

            {/* Info bancária */}
            {!isCash && (account.agency || account.account_number) && (
                <div style={S.infoGrid}>
                    {account.agency && (
                        <div>
                            <div style={S.infoLabel}>Agência</div>
                            <div style={S.infoValue}>{account.agency}</div>
                        </div>
                    )}
                    {account.account_number && (
                        <div>
                            <div style={S.infoLabel}>Conta</div>
                            <div style={S.infoValue}>{account.account_number}</div>
                        </div>
                    )}
                </div>
            )}

            {/* Stats 30d */}
            <div style={S.statsRow}>
                <div style={S.statIn}>
                    <div style={S.statArrowIn}>↑ Entradas (30d)</div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>{fmt(account.income_30d)}</div>
                </div>
                <div style={S.statOut}>
                    <div style={S.statArrowOut}>↑ Saídas (30d)</div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>{fmt(account.expense_30d)}</div>
                </div>
            </div>

            {/* Ações */}
            <div style={S.cardActions}>
                <button
                    style={S.btnCardSecondary}
                    onClick={() => window.location.href = '/financial/open-finance'}
                >
                    <i className="fas fa-list" style={{ marginRight: 5 }} />Extrato
                </button>
                <button style={{ ...S.btnCardSecondary, color: '#ef4444', borderColor: '#fecaca' }}
                    onClick={() => {
                        if (window.confirm(`Remover "${account.name}"?`)) onDelete(account.id);
                    }}>
                    <i className="fas fa-trash" />
                </button>
            </div>
        </div>
    );
}


// ── Componente Modal Transferência ───────────────────────────────────────────
function TransferModal({ show, onHide, onSaved, accounts }) {
    const [form, setForm] = React.useState({ source_account_id: '', destination_account_id: '', amount: '', date: new Date().toISOString().split('T')[0] });
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (show) setForm({ source_account_id: '', destination_account_id: '', amount: '', date: new Date().toISOString().split('T')[0] });
    }, [show]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.source_account_id || !form.destination_account_id) {
            toastify.error('Selecione as contas de origem e destino.', { position: 'top-center' });
            return;
        }
        if (form.source_account_id === form.destination_account_id) {
            toastify.error('As contas de origem e destino devem ser diferentes.', { position: 'top-center' });
            return;
        }
        const val = parseFloat(String(form.amount).replace(',', '.')) || 0;
        if (val <= 0) {
            toastify.error('O valor da transferência deve ser maior que zero.', { position: 'top-center' });
            return;
        }

        setLoading(true);
        try {
            await transferBetweenAccounts({
                source_account_id: form.source_account_id,
                destination_account_id: form.destination_account_id,
                amount: val,
                date: form.date,
            });
            toastify.success('Transferência realizada com sucesso!', { position: 'top-center' });
            onSaved();
            onHide();
        } catch (err) {
            toastify.error('Erro ao realizar transferência.', { position: 'top-center' });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onHide()}>
            <div style={S.modal}>
                <button style={S.modalClose} onClick={onHide}>×</button>
                <div style={S.modalTitle}><i className="fas fa-exchange-alt" style={{ marginRight: 10, color: '#2563eb' }}/> Transferência entre Caixas</div>

                <div style={S.fieldGroup}>
                    <label style={S.fieldLabel}>Caixa de Origem (Saiu de)</label>
                    <select style={S.input} name="source_account_id" value={form.source_account_id} onChange={handleChange}>
                        <option value="">-- Selecione a conta origem --</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                </div>

                <div style={S.fieldGroup}>
                    <label style={S.fieldLabel}>Caixa de Destino (Entrou em)</label>
                    <select style={S.input} name="destination_account_id" value={form.destination_account_id} onChange={handleChange}>
                        <option value="">-- Selecione a conta destino --</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                </div>

                <div style={{ ...S.row3, gridTemplateColumns: '1fr 1fr' }}>
                    <div style={S.fieldGroup}>
                        <label style={S.fieldLabel}>Valor (R$)</label>
                        <input style={S.input} name="amount" value={form.amount} placeholder="0,00" onChange={handleChange} />
                    </div>
                    <div style={S.fieldGroup}>
                        <label style={S.fieldLabel}>Data</label>
                        <input style={S.input} type="date" name="date" value={form.date} onChange={handleChange} />
                    </div>
                </div>

                <div style={S.modalFooter}>
                    <button style={S.btnCancel} onClick={onHide} disabled={loading}>Cancelar</button>
                    <button style={S.btnSubmit} onClick={handleSubmit} disabled={loading}>
                        {loading ? <i className="fas fa-spinner fa-spin" /> : 'Transferir Dinheiro'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Caixas() {
    const context = React.useContext(AppContext);
    const [summary, setSummary] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [showModal, setShowModal] = React.useState(false);
    const [showTransferModal, setShowTransferModal] = React.useState(false);

    const loadSummary = async () => {
        setLoading(true);
        try {
            const data = await getBankAccountsSummary();
            setSummary(data);
        } catch (err) {
            console.error('Erro ao carregar caixas:', err);
            toastify.error('Erro ao carregar caixas. Verifique a conexão com a API.', { position: 'top-center' });
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => { loadSummary(); }, [context.currentBranch]);

    const handleDelete = async (accountId) => {
        try {
            await deleteBankAccount(accountId);
            toastify.success('Caixa removido.', { position: 'top-center' });
            loadSummary();
        } catch (err) {
            toastify.error('Erro ao remover caixa.', { position: 'top-center' });
        }
    };

    const accounts = summary ? summary.accounts : [];
    const bankAccounts = accounts.filter(a => a.account_type !== 'cash');
    const cashAccounts = accounts.filter(a => a.account_type === 'cash');

    return (
        <div style={S.page}>
            {/* Breadcrumb */}
            <div style={S.breadcrumb}>
                <span style={S.breadcrumbLink} onClick={() => window.location.href = '/financial/home'}>Financeiro</span>
                {' › '}
                <span>Caixas</span>
            </div>

            <h1 style={S.pageTitle}>Caixas</h1>
            <p style={S.pageSubtitle}>Contas bancárias e caixas físicos da empresa</p>

            {/* Sumário consolidado */}
            <div style={S.summaryRow}>
                <div style={S.consolidatedCard}>
                    <div style={S.consolidatedLabel}>Saldo Total Consolidado</div>
                    <div style={S.consolidatedValue}>{fmt(summary ? summary.total_balance : 0)}</div>
                    <div style={S.consolidatedDesc}>Somatório de todos os caixas e contas bancárias ativas</div>
                </div>

                <div style={S.statCard}>
                    <div style={S.statNum}>{summary ? `${summary.open_finance_connected} / ${summary.open_finance_total}` : '–'}</div>
                    <div style={S.statLabel}>Bancos Conectados</div>
                    <div style={S.statSub}>via Open Finance</div>
                </div>

                <div style={S.statCard}>
                    <div style={S.statNum}>{summary ? summary.physical_accounts : '–'}</div>
                    <div style={S.statLabel}>Caixas Físicos</div>
                    <div style={S.statSub}>conferência manual</div>
                </div>
            </div>

            {/* Seção contas */}
            <div style={S.sectionRow}>
                <div style={S.sectionTitle}>Caixas e Contas Bancárias</div>
                <div style={S.btnRow}>
                    <button style={S.btnOutline} onClick={() => setShowTransferModal(true)}>
                        <i className="fas fa-exchange-alt" color="#2563eb" /> Transferir entre caixas
                    </button>
                    <button style={S.btnPrimary} onClick={() => setShowModal(true)}>
                        <i className="fas fa-plus" /> Novo caixa
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={S.spinner}>
                    <i className="fas fa-spinner fa-spin fa-2x" />
                    <div style={{ marginTop: 12 }}>Carregando caixas...</div>
                </div>
            ) : accounts.length === 0 ? (
                <div style={S.emptyState}>
                    <i className="fas fa-university" style={{ fontSize: 40, marginBottom: 12 }} />
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>Nenhum caixa cadastrado</div>
                    <div style={{ fontSize: 13, marginTop: 6 }}>Clique em "Novo caixa" para começar.</div>
                </div>
            ) : (
                <div>
                    {bankAccounts.length > 0 && (
                        <div style={S.cardsGrid}>
                            {bankAccounts.map(acc => (
                                <AccountCard key={acc.id} account={acc} onDelete={handleDelete} />
                            ))}
                        </div>
                    )}

                    {cashAccounts.length > 0 && (
                        <div>
                            <div style={{ marginTop: 28, marginBottom: 14, fontWeight: 600, color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                Caixas Físicos
                            </div>
                            <div style={S.cardsGrid}>
                                {cashAccounts.map(acc => (
                                    <AccountCard key={acc.id} account={acc} onDelete={handleDelete} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Botão + flutuante para adicionar caixa físico se lista vazia */}
            {!loading && cashAccounts.length === 0 && bankAccounts.length > 0 && (
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
                    <button
                        style={{ ...S.btnOutline, border: '2px dashed #cbd5e1', color: '#94a3b8', padding: '14px 24px' }}
                        onClick={() => setShowModal(true)}
                    >
                        <i className="fas fa-plus-circle" style={{ marginRight: 6 }} />
                        Adicionar caixa físico
                    </button>
                </div>
            )}

            <NovoCaixaModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSaved={loadSummary}
            />
            
            <TransferModal
                show={showTransferModal}
                onHide={() => setShowTransferModal(false)}
                onSaved={loadSummary}
                accounts={accounts}
            />
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/views/AppViews/Financial/Caixas/index.jsx